<template>
    <b-form class="create-tenant-form" @submit.stop.prevent="onSubmit">
        <div v-if="isUpdateMode" class="multiselect-container group-wrapper">
            <label>Tenant</label>
            <multiselect
                v-model="form.tenant"
                :options="tenants"
                :clear-on-select="false"
                :preserve-search="true"
                placeholder=""
                :preselect-first="true"
                label="name"
                track-by="_id"
                :show-labels="false"
                :loading="loadingTenants"
                @close="checkForError('tenant')"
                @select="setFormDataByTenant"
            />
            <p class="error error-message">{{ validationErrors.tenant }}</p>
        </div>
        <div v-if="!isUpdateMode" class="group-wrapper">
            <label>Tenant Name</label>

            <div class="tenant-name-input-wrapper">
                <b-form-input id="tenant-name" v-model="form.name" @blur="checkForError('name')" />
                <p class="error">{{ validationErrors.name }}</p>
            </div>
        </div>

        <div class="multiselect-container group-wrapper">
            <label>Clinic country</label>
            <multiselect
                v-model="form.country"
                :options="countries"
                :clear-on-select="false"
                :preserve-search="true"
                placeholder=""
                label="name"
                track-by="code"
                :show-labels="false"
                @close="checkForError('country')"
            />
            <p class="error error-message">{{ validationErrors.country }}</p>
        </div>

        <div class="multiselect-container group-wrapper">
            <label>Clinic city</label>
            <multiselect
                v-model="form.city"
                :options="cities"
                :clear-on-select="false"
                :preserve-search="true"
                placeholder=""
                :preselect-first="true"
                :show-labels="false"
                @close="checkForError('city')"
            />
            <p class="error error-message">{{ validationErrors.city }}</p>
        </div>

        <div class="group-wrapper">
            <label class="custom-label"> EmrType </label>

            <b-dropdown v-model="emrTypeOptions">
                <template v-slot:button-content>
                    <span>{{ form.emrType || 'Select EmrType' }}</span>
                </template>

                <b-dropdown-item
                    v-for="name in emrTypeOptions"
                    :key="name"
                    @click="form.emrType = name"
                >
                    <span> {{ name }} </span>
                </b-dropdown-item>
            </b-dropdown>
        </div>

        <div class="group-wrapper">
            <label>Products</label>
            <b-form-group class="product-options">
                <b-form-checkbox-group
                    v-model="form.products"
                    :options="form.productOptions"
                    @change="checkForError('products')"
                />
            </b-form-group>
            <p class="error error-message">{{ validationErrors.products }}</p>
        </div>

        <div class="group-wrapper">
            <label>Working hours</label>

            <div class="working-hours-form-container">
                <div class="working-hours-input-wrapper">
                    <b-form-timepicker
                        id="working-hours-start"
                        v-model="form.workingHours.start"
                        type="time"
                        locale="en"
                    />
                    -
                    <b-form-timepicker
                        id="working-hours-finish"
                        v-model="form.workingHours.finish"
                        type="time"
                        locale="en"
                    />
                </div>
                <p class="error">{{ validationErrors.workingHours }}</p>
            </div>
        </div>

        <div class="group-wrapper">
            <label>Invoke pipeline every n-run</label>

            <div class="tenant-name-input-wrapper">
                <b-form-input
                    id="invokePipeLineEveryNRun"
                    v-model.number="form.invokePipeLineEveryNRun"
                    type="number"
                    required
                    @blur="checkForError('invokePipeLineEveryNRun')"
                />
                <p class="error">{{ validationErrors.invokePipeLineEveryNRun }}</p>
            </div>
        </div>

        <div class="group-wrapper">
            <label>Pipeline frequency by time range</label>

            <div class="pipeline-freq-inputs-container">
                <div
                    v-for="(rangeSetting, index) in form.pipelineFrequencyByTimeRange"
                    :key="index"
                    class="pipeline-freq-input-wrapper"
                >
                    <b-form-timepicker
                        :id="`freq-time-range-${index}-start`"
                        v-model="rangeSetting.start"
                        type="time"
                        locale="en"
                    />
                    -
                    <b-form-timepicker
                        :id="`freq-time-range-${index}-finish`"
                        v-model="rangeSetting.finish"
                        type="time"
                        locale="en"
                    />
                    <b-form-input
                        :id="`freq-time-range-${index}-freq`"
                        v-model.number="rangeSetting.frequency"
                        type="number"
                    />
                    <i class="fa fa-close" @click.stop="removePipelineFreqTimeRange(index)"></i>
                </div>

                <span class="add-range" @click="addPipelineFreqTimeRange">
                    <i class="fa fa-plus"></i> Add range
                </span>

                <p class="error">{{ validationErrors.pipelineFreqRanges }}</p>
            </div>
        </div>

        <div class="group-wrapper">
            <label>PipelineDebounceInHours</label>

            <div class="tenant-name-input-wrapper">
                <b-form-input
                    id="pipelineDebounceInHours"
                    v-model.number="form.pipelineDebounceInHours"
                    type="number"
                    step=".1"
                    @blur="checkForError('pipelineDebounceInHours')"
                />
                <p class="error">{{ validationErrors.pipelineDebounceInHours }}</p>
            </div>
        </div>

        <div class="group-wrapper">
            <label>Activate AI (CHLOE EQ Insights)</label>
            <b-checkbox v-model="form.isAiActive" switch size="lg" />
        </div>

        <div class="group-wrapper">
            <label> Active AI (OQ) </label>
            <b-checkbox v-model="form.isOqAiActive" switch size="lg" />
        </div>

        <div class="group-wrapper">
            <label> New CHLOE interface </label>
            <b-checkbox v-model="form.showNewInterface" switch size="lg" />
        </div>

        <div class="group-wrapper">
            <label> IRB Clinic </label>
            <b-checkbox v-model="form.isIRBClinic" switch size="lg" />
        </div>

        <div class="group-wrapper">
            <label> Clinic Ranking </label>
            <b-checkbox v-model="form.enableClinicRanking" switch size="lg" />
        </div>

        <!--        <div class="group-wrapper">-->
        <!--            <label> Export Treatment Data </label>-->
        <!--            <b-checkbox v-model="form.enableClinicTreatmentExport" switch size="lg" />-->
        <!--        </div>-->

        <div id="submit" class="group-wrapper">
            <b-button type="submit" variant="primary">Submit</b-button>
        </div>
    </b-form>
</template>

<script>
import BackendProvider from '../../../../business/services/BackendProvider'
import UIUtils from '../../../../business/utils/UIUtils'
import { EMR_TYPES } from '../../../../business/const'
import Countries from 'i18n-iso-countries'
import Multiselect from 'vue-multiselect'
import cityTimezones from 'city-timezones'

Countries.registerLocale(require('i18n-iso-countries/langs/en.json'))

export default {
    name: 'CreateUpdateTenantForm',
    components: { Multiselect },
    props: {
        isUpdateMode: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            form: {
                name: '',
                country: '',
                countryCode: '',
                city: '',
                products: ['EQ'],
                productOptions: [
                    { text: 'EQ', value: 'EQ' },
                    { text: 'KPI', value: 'KPI' },
                    { text: 'OQ', value: 'OQ' },
                    { text: 'DOCTOR', value: 'DOCTOR' }
                ],
                isAiActive: false,
                isIRBClinic: false,
                isOqAiActive: false,
                enableClinicRanking: false,
                // enableClinicTreatmentExport: false,
                showNewInterface: false,
                emrType: '',
                invokePipeLineEveryNRun: '',
                pipelineDebounceInHours: '',
                workingHours: {
                    start: '', //  a string in the format of 'HH:mm:ss'
                    finish: ''
                },
                pipelineFrequencyByTimeRange: [
                    // {
                    //     start: '',
                    //     finish: '',
                    //     frequency: 1
                    // }
                ]
            },
            emptyCountryError: '',
            networkResponseError: '',
            validationErrors: {
                tenant: '',
                name: '',
                city: '',
                country: '',
                products: '',
                workingHours: '',
                pipelineFreqRanges: '',
                invokePipeLineEveryNRun: '',
                pipelineDebounceInHours: ''
            },
            tenants: [],
            cities: [],
            loadingTenants: false,
            emrTypeOptions: EMR_TYPES
        }
    },
    computed: {
        countries() {
            const countries = Countries.getNames('en', { select: 'official' })
            return Object.keys(countries).map(code => ({
                name: countries[code],
                code
            }))
        },
        isFormInValid() {
            return Object.values(this.validationErrors).some(error => !!error)
        }
    },
    watch: {
        'form.country'(newCountry) {
            const countryCode = newCountry ? newCountry.code : null

            this.form.isAiActive = countryCode !== 'US'

            this.cities = countryCode
                ? cityTimezones.cityMapping
                      .filter(x => x.iso2 === countryCode)
                      .map(city => city.city)
                : []
            this.form.city = ''

            this.form.countryCode = countryCode
        }
    },
    async mounted() {
        if (this.isUpdateMode) {
            await this.loadTenants()
        }
    },
    methods: {
        async loadTenants() {
            this.loadingTenants = true
            this.tenants = await BackendProvider.getAllTenants()
            this.loadingTenants = false
        },
        async onSubmit() {
            this.validateForm()
            if (this.isFormInValid) {
                return
            }

            UIUtils.showLoadingMask()
            try {
                if (this.isUpdateMode) {
                    const updatedTenant = await this.updateTenant()
                    this.$bvModal.hide('update_tenant')
                    UIUtils.toast(`tenant updated: ${updatedTenant._id}`, 'message', 10 * 1000)
                } else {
                    const createdTenant = await this.createTenant()
                    this.$bvModal.hide('create_tenant')
                    UIUtils.toast(`tenant created: ${createdTenant._id}`, 'message', 10 * 1000)
                }
            } catch (e) {
                UIUtils.toast(e.message, 'danger', 10 * 1000)
            }
            UIUtils.hideLoadingMask()
        },
        async createTenant() {
            const {
                name,
                countryCode,
                city,
                workingHours,
                isAiActive,
                products,
                invokePipeLineEveryNRun,
                pipelineDebounceInHours,
                pipelineFrequencyByTimeRange,
                emrType,
                showNewInterface,
                isIRBClinic,
                isOqAiActive,
                enableClinicRanking
            } = this.form

            const createTenantDto = {
                name,
                city,
                workingHours,
                products,
                country: countryCode,
                settings: {
                    isAiActive,
                    agentHandler: {},
                    showNewInterface,
                    isIRBClinic,
                    isOqAiActive,
                    enableClinicRanking
                }
            }
            if (emrType !== '') {
                createTenantDto.emrType = emrType
            }
            if (!isNaN(invokePipeLineEveryNRun) && invokePipeLineEveryNRun !== '') {
                createTenantDto.settings.agentHandler.invokePipeLineEveryNRun = Number(invokePipeLineEveryNRun)
            }
            if (!isNaN(pipelineDebounceInHours) && pipelineDebounceInHours !== '') {
                createTenantDto.settings.agentHandler.pipelineDebounceInHours = Number(pipelineDebounceInHours)
            }

            if (pipelineFrequencyByTimeRange.length) {
                createTenantDto.settings.agentHandler.pipelineFrequencyByTimeRange =
                    pipelineFrequencyByTimeRange
            }

            if (Object.keys(createTenantDto.settings.agentHandler).length === 0) {
                delete createTenantDto.settings.agentHandler
            }
            console.log(createTenantDto, 'createTenantDto')

            return await BackendProvider.createTenant(createTenantDto)
        },
        async updateTenant() {
            const {
                countryCode,
                city,
                workingHours,
                isAiActive,
                products,
                tenant,
                invokePipeLineEveryNRun,
                pipelineDebounceInHours,
                pipelineFrequencyByTimeRange,
                emrType,
                showNewInterface,
                isIRBClinic,
                isOqAiActive,
                enableClinicRanking
            } = this.form

            const updateTenantDto = {
                products,
                city,
                workingHours,
                country: countryCode,
                settings: {
                    isAiActive,
                    agentHandler: {},
                    showNewInterface,
                    isIRBClinic,
                    isOqAiActive,
                    enableClinicRanking
                }
            }

            if (emrType !== '') {
                updateTenantDto.emrType = emrType
            }
            if (!isNaN(invokePipeLineEveryNRun) && invokePipeLineEveryNRun !== '') {
                updateTenantDto.settings.agentHandler.invokePipeLineEveryNRun =
                    invokePipeLineEveryNRun
            }
            if (!isNaN(pipelineDebounceInHours) && pipelineDebounceInHours !== '') {
                updateTenantDto.settings.agentHandler.pipelineDebounceInHours =
                    pipelineDebounceInHours
            }

            if (pipelineFrequencyByTimeRange.length) {
                updateTenantDto.settings.agentHandler.pipelineFrequencyByTimeRange =
                    pipelineFrequencyByTimeRange
            }

            if (Object.keys(updateTenantDto.settings.agentHandler).length === 0) {
                delete updateTenantDto.settings.agentHandler
            }

            return await BackendProvider.updateTenant(updateTenantDto, tenant._id)
        },
        checkForError(id) {
            switch (id) {
                case 'tenant':
                    this.validationErrors.tenant =
                        this.isUpdateMode && !this.form.tenant ? 'Tenant a required field' : ''
                    return
                case 'country':
                    this.validationErrors.country = !this.form.country
                        ? 'Clinic Country is a required field'
                        : ''
                    return
                case 'name':
                    this.validationErrors.name =
                        !this.isUpdateMode && !this.form.name ? 'Name is a required field' : ''
                    return
                case 'products':
                    this.validationErrors.products =
                        this.form.products.length === 0 ? 'Products is a required field' : ''
                    return
                case 'city':
                    this.validationErrors.city = this.form.city
                        ? ''
                        : 'Clinic City is a required field'
                    return
                case 'invokePipeLineEveryNRun':
                    this.validationErrors.invokePipeLineEveryNRun =
                        this.form.invokePipeLineEveryNRun &&
                        this.form.invokePipeLineEveryNRun > 0 &&
                        this.form.invokePipeLineEveryNRun <= 100
                            ? ''
                            : 'Please, enter value from 1 to 100'
                    return
                case 'workingHours':
                    this.validationErrors.workingHours = ''
                    if (this.form.workingHours.start && this.form.workingHours.finish) {
                        const isStartBeforeFinish = this.checkIfStartBeforeFinish(
                            this.form.workingHours.start,
                            this.form.workingHours.finish
                        )
                        if (!isStartBeforeFinish) {
                            this.validationErrors.workingHours = 'Workday must start before finish'
                        }
                    }
                    return
                case 'pipelineFreqRanges':
                    this.validationErrors.pipelineFreqRanges = ''
                    const nonEmptyRanges = this.form.pipelineFrequencyByTimeRange.filter(
                        range => range.start && range.finish && range.frequency
                    )
                    // validate start before finish
                    nonEmptyRanges.forEach(range => {
                        if (!this.checkIfStartBeforeFinish(range.start, range.finish)) {
                            this.validationErrors.pipelineFreqRanges =
                                'Time range must start before finish'
                        }
                    })
                    // validate no overlap
                    if (this.checkIfOverlap(nonEmptyRanges)) {
                        this.validationErrors.pipelineFreqRanges = 'Ranges must not overlap'
                    }

                    this.form.pipelineFrequencyByTimeRange = nonEmptyRanges
                    return
            }
        },
        validateForm() {
            Object.keys(this.validationErrors).forEach(key => this.checkForError(key))
        },
        setFormDataByTenant(tenant) {
            if (!tenant) return

            const { country, city, workingHours, products, emrType, settings } = tenant

            if (country) {
                this.form.countryCode = country
                this.form.country = {
                    name: Countries.getName(country, 'en'),
                    code: country
                }
            }
            if (city) {
                this.$nextTick(() => {
                    this.form.city = city
                })
            }
            if (workingHours) {
                this.form.workingHours = workingHours
            }
            if (products) {
                this.form.products = products
            }
            if (emrType) {
                this.form.emrType = emrType
            }

            if (settings) {
                const {
                    isAiActive,
                    agentHandler,
                    showNewInterface,
                    isIRBClinic,
                    isOqAiActive,
                    enableClinicRanking
                } = settings

                this.form.isAiActive = isAiActive
                this.form.showNewInterface = showNewInterface
                this.form.isIRBClinic = isIRBClinic
                this.form.isOqAiActive = isOqAiActive
                this.form.enableClinicRanking = enableClinicRanking
                // this.form.enableClinicTreatmentExport = enableClinicTreatmentExport

                if (agentHandler) {
                    const {
                        invokePipeLineEveryNRun,
                        pipelineDebounceInHours,
                        pipelineFrequencyByTimeRange
                    } = agentHandler

                    this.form.invokePipeLineEveryNRun = invokePipeLineEveryNRun
                    this.form.pipelineDebounceInHours = pipelineDebounceInHours

                    if (pipelineFrequencyByTimeRange && pipelineFrequencyByTimeRange.length) {
                        this.form.pipelineFrequencyByTimeRange = pipelineFrequencyByTimeRange
                    }
                }
            }
        },
        addPipelineFreqTimeRange() {
            this.form.pipelineFrequencyByTimeRange.push({
                start: '',
                finish: '',
                frequency: 1
            })
        },
        removePipelineFreqTimeRange(index) {
            this.form.pipelineFrequencyByTimeRange.splice(index, 1)
        },
        checkIfStartBeforeFinish(start, finish) {
            const [hoursStart] = start.split(':')
            const [hoursFinish] = finish.split(':')

            return hoursStart < hoursFinish
        },
        convertTimeToSeconds(timeString) {
            const [hours, minutes, seconds] = timeString.split(':')

            return Number(hours) * 60 * 60 + Number(minutes) * 60 + Number(seconds)
        },
        checkIfOverlap(ranges) {
            const rangesSec = ranges.map(range => ({
                start: this.convertTimeToSeconds(range.start),
                finish: this.convertTimeToSeconds(range.finish)
            }))

            for (const currentIdx in rangesSec) {
                const min = rangesSec[currentIdx].start
                const max = rangesSec[currentIdx].finish

                const otherRanges = rangesSec.filter((range, index) => index !== currentIdx)

                for (const range of otherRanges) {
                    if (
                        (range.start > min && range.start < max) ||
                        (range.finish > min && range.finish < max)
                    ) {
                        return true
                    }
                }
            }

            return false
        }
    }
}
</script>

<style lang="scss" scoped>
.create-tenant-form {
    width: 50rem;
    @include input-number-no-arrows();

    .group-wrapper {
        display: grid;
        grid-template-columns: 40% 60%;
        align-items: center;
        padding: 0.25rem;
        margin-bottom: 0.5rem;

        .error {
            color: $red;
            font-size: 0.9rem;
            margin: 0;
        }

        .tenant-name-input-wrapper {
            display: flex;
            flex-direction: column;
            justify-content: flex-end;
        }

        .working-hours-input-wrapper {
            display: grid;
            grid-template-columns: 1fr auto 1fr;
            align-items: center;
            column-gap: 8px;
        }

        .pipeline-freq-inputs-container {
            .pipeline-freq-input-wrapper {
                display: grid;
                grid-template-columns: 1fr min-content 1fr 35px 5px;
                align-items: center;
                column-gap: 8px;

                &:not(:last-child) {
                    margin-bottom: 8px;
                }
            }

            .add-range {
                cursor: pointer;
            }
        }

        .buttons-wrapper {
            display: flex;
            justify-content: space-between;
        }
        .custom-label {
            padding: 0.5rem 0 0 0;
        }
        .error-message {
            margin-left: 100%;
            width: 100%;
        }
        .product-options {
            padding-top: 0.25rem;
        }
    }

    #submit.group-wrapper {
        display: flex;
        justify-content: center;
    }
}
</style>

<style lang="scss">
.create-tenant-form {
    .dropdown-menu {
        text-align: center;
        min-width: 12rem;
        overflow: auto;
        height: 149px;
    }
}
//override vue multiselect component styles
.multiselect-container {
    &.group-wrapper {
        .multiselect {
            z-index: 2;
            font-size: 0.9rem;
            position: relative;

            .multiselect__tags {
                background-color: $dark-2;
                border-radius: 0.3rem;
                border-color: $light;

                .multiselect__single {
                    color: $light-2;
                    background-color: transparent;
                    font-size: 0.9rem;
                }

                .multiselect__placeholder {
                    font-size: 0.9rem;
                }

                .multiselect__input {
                    background-color: $dark-2;
                    color: $light;
                    font-size: 0.9rem;
                }
            }

            .multiselect__content-wrapper {
                position: absolute;
                max-height: 10rem;
                background-color: $dark-2;
                border-color: $light;
                border-radius: 0.3rem;

                .multiselect__content {
                    background-color: $dark-2;
                    color: $light-2;
                    max-height: 10rem;

                    .multiselect__element {
                        .multiselect__option--highlight {
                            background-color: $blue;
                        }

                        .multiselect__option--selected {
                            background-color: $blue;
                        }
                    }
                }
            }
        }
    }

    .multiselect.multiselect--active {
        z-index: 5;
    }
}

.multiselect__spinner {
    background: transparent;
    margin-right: 1rem;
    &::after,
    &::before {
        border-top-color: white;
    }
}
.create-tenant-form,
.update-tenant-form {
    .b-form-spinbutton.form-control {
        &.focus {
            color: unset;
            background-color: unset;
        }
    }
}
</style>
